import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  MessageSquare,
  Clock,
  CheckCircle,
  Building2,
  Users,
  Headphones,
  ArrowRight
} from 'lucide-react';
import { LandingLayout } from '../components/LandingLayout';
import {
  AnimatedSection,
  StaggerContainer,
  StaggerItem,
  ScaleIn
} from '../components/AnimatedSection';
import { useLanguage } from '../i18n';

const offices = [
  {
    city: 'Headquarters',
    address: '11 Av. de l\'Harmonie, Park Plaza II Bât. B',
    region: 'Villeneuve-d\'Ascq, 59650',
    country: 'FR'
  },
];

export const ContactPage = () => {
  const { t, dir } = useLanguage();

  const initialForm = {
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: '',
    message: '',
    interest: 'demo'
  };
  const [formState, setFormState] = useState(initialForm);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const contactMethods = [
    {
      icon: Mail,
      title: t.contact.emailUs,
      description: t.contact.emailDesc,
      value: 'houcinemedkit.ai@gmail.com',
      action: 'mailto:houcinemedkit.ai@gmail.com'
    },
    {
      icon: Phone,
      title: t.contact.callUs,
      description: t.contact.callDesc,
      value: '+33 1 23 45 67 89',
      action: 'tel:+33123456789'
    },
    {
      icon: MessageSquare,
      title: t.contact.liveChat,
      description: t.contact.chatDesc,
      value: t.contact.startChat,
      action: 'mailto:houcinemedkit.ai@gmail.com'
    },
  ];

  const reasons = [
    { icon: Users, text: t.contact.dedicatedTeam },
    { icon: Clock, text: t.contact.responseTime },
    { icon: Headphones, text: t.contact.experts },
    { icon: Building2, text: t.contact.enterpriseSolutions },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formState)
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.status !== 'success') {
        setStatus('error');
        setErrorMessage(
          data.message || 'Something went wrong. Please try again or email us directly.'
        );
        return;
      }

      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMessage('Could not reach the server. Please try again in a moment.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormState(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <LandingLayout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden" dir={dir}>
        <div className="absolute inset-0 bg-gradient-to-b from-forest/5 to-transparent" />
        <div className="absolute top-40 -end-40 w-[400px] h-[400px] bg-forest/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-bark-700 leading-tight mb-6">
              {t.contact.title}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-forest to-forest-400">
                {t.contact.titleHighlight}
              </span>
            </h1>
            <p className="text-xl text-bark-700/60">
              {t.contact.description}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-12 lg:py-16" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <StaggerContainer className="grid md:grid-cols-3 gap-6">
            {contactMethods.map((method, index) => (
              <StaggerItem key={index}>
                <a
                  href={method.action}
                  className="group block bg-sand-50 rounded-2xl p-8 border border-sand-300 hover:border-forest/30 hover:shadow-lg transition-all"
                >
                  <div className="w-14 h-14 rounded-xl bg-forest/10 flex items-center justify-center mb-6 group-hover:bg-forest/20 transition-colors">
                    <method.icon className="w-7 h-7 text-forest" />
                  </div>
                  <h3 className="text-xl font-bold text-bark-700 mb-2">{method.title}</h3>
                  <p className="text-bark-700/60 mb-4">{method.description}</p>
                  <span className="text-forest font-medium group-hover:underline">
                    {method.value}
                  </span>
                </a>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-16 lg:py-24 bg-sand-300/30" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <AnimatedSection direction="left">
              <div className="bg-sand-50 rounded-3xl p-8 lg:p-12 shadow-xl">
                <h2 className="text-2xl lg:text-3xl font-bold text-bark-700 mb-2">
                  {t.contact.sendMessage}
                </h2>
                <p className="text-bark-700/60 mb-8">
                  {t.contact.sendMessageDesc}
                </p>

                {status === 'success' ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-20 h-20 rounded-full bg-forest/10 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-forest" />
                    </div>
                    <h3 className="text-2xl font-bold text-bark-700 mb-3">
                      {t.contact.sent}
                    </h3>
                    <p className="text-bark-700/60 mb-6">
                      {t.contact.sentDesc}
                    </p>
                    <button
                      onClick={() => {
                        setStatus('idle');
                        setFormState(initialForm);
                      }}
                      className="text-forest font-medium hover:underline"
                    >
                      {t.contact.sendAnother}
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-bark-700 mb-2">
                          {t.contact.firstName} *
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={formState.firstName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-sand-300 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none transition-all bg-sand-50"
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-bark-700 mb-2">
                          {t.contact.lastName} *
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={formState.lastName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-sand-300 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none transition-all bg-sand-50"
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-bark-700 mb-2">
                        {t.contact.email} *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formState.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-sand-300 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none transition-all bg-sand-50"
                        placeholder="john@clinic.com"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-bark-700 mb-2">
                          {t.contact.company}
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formState.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-sand-300 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none transition-all bg-sand-50"
                          placeholder="City Medical Center"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-bark-700 mb-2">
                          {t.contact.role}
                        </label>
                        <input
                          type="text"
                          name="role"
                          value={formState.role}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-sand-300 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none transition-all bg-sand-50"
                          placeholder="Physician"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-bark-700 mb-2">
                        {t.contact.interest} *
                      </label>
                      <select
                        name="interest"
                        value={formState.interest}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-sand-300 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none transition-all bg-sand-50"
                      >
                        <option value="demo">{t.contact.interests.demo}</option>
                        <option value="pricing">{t.contact.interests.pricing}</option>
                        <option value="enterprise">{t.contact.interests.enterprise}</option>
                        <option value="integration">{t.contact.interests.integration}</option>
                        <option value="support">{t.contact.interests.support}</option>
                        <option value="other">{t.contact.interests.other}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-bark-700 mb-2">
                        {t.contact.message} *
                      </label>
                      <textarea
                        name="message"
                        value={formState.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="w-full px-4 py-3 rounded-xl border border-sand-300 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none transition-all resize-none bg-sand-50"
                        placeholder="Tell us about your needs..."
                      />
                    </div>

                    {status === 'error' && (
                      <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                        {errorMessage}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="w-full py-4 rounded-xl bg-bark-700 text-white font-semibold hover:bg-bark-600 transition-all shadow-lg shadow-bark-700/25 flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                      {status === 'submitting' ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          {t.contact.sending}
                        </>
                      ) : (
                        <>
                          {t.contact.send}
                          <Send className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </AnimatedSection>

            {/* Right Side Info */}
            <AnimatedSection direction="right" className="space-y-12">
              {/* Why Contact Us */}
              <div>
                <h3 className="text-xl font-bold text-bark-700 mb-6">
                  {t.contact.whyContact}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {reasons.map((reason, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-forest/10 flex items-center justify-center flex-shrink-0">
                        <reason.icon className="w-5 h-5 text-forest" />
                      </div>
                      <span className="text-bark-700 text-sm">{reason.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Office Locations */}
              <div>
                <h3 className="text-xl font-bold text-bark-700 mb-6">
                  {t.contact.offices}
                </h3>
                <div className="space-y-6">
                  {offices.map((office, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-forest/10 flex items-center justify-center flex-shrink-0 mt-1">
                        <MapPin className="w-5 h-5 text-forest" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-bark-700">{office.city}</h4>
                        <p className="text-bark-700/60 text-sm">
                          {office.address}
                          <br />
                          {office.region}
                          <br />
                          {office.country}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Demo CTA */}
              <div className="bg-gradient-to-br from-bark-700 to-bark-600 rounded-2xl p-8 text-white">
                <h3 className="text-xl font-bold mb-3">
                  {t.contact.quickDemo}
                </h3>
                <p className="text-white/90 mb-6">
                  {t.contact.quickDemoDesc}
                </p>
                <a
                  href="mailto:houcinemedkit.ai@gmail.com?subject=Demo%20request%20-%20MedKit.ai"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-accent text-bark-700 font-semibold hover:bg-accent/90 transition-all"
                >
                  {t.contact.scheduleDemo}
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-20 lg:py-28" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ScaleIn>
            <div className="text-center">
              <h2 className="text-3xl lg:text-4xl font-bold text-bark-700 mb-4">
                {t.contact.quickAnswers}
              </h2>
              <p className="text-xl text-bark-700/60 mb-8">
                {t.contact.quickAnswersDesc}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-sand-300/30 text-bark-700 font-semibold hover:bg-sand-300/50 transition-all"
                >
                  {t.contact.viewFaq}
                </a>
                <a
                  href="mailto:houcinemedkit.ai@gmail.com"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-bark-700 text-white font-semibold hover:bg-bark-600 transition-all shadow-lg shadow-bark-700/25"
                >
                  {t.contact.helpCenter}
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
          </ScaleIn>
        </div>
      </section>
    </LandingLayout>
  );
};
